import { useCallback, useState, useRef } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, X, Heart } from 'lucide-react';

let stripePromise: Promise<Stripe | null> | null = null;
function getStripe() {
  if (!stripePromise) {
    stripePromise = loadStripe('pk_live_51TFApjQdZC8eEKkZz84DNDXjWYDTA1diKyCZoQLChnmdazgHZZJ3XPyILlr08yIGdiHbTKp8jUKww5GzQItmRdxY00ToUu2TGy');
  }
  return stripePromise;
}

interface EmbeddedCheckoutProps {
  moduleCode: string;
  quantity?: number;
  onClose: () => void;
}

export default function EmbeddedCheckoutComponent({ moduleCode, quantity = 1, onClose }: EmbeddedCheckoutProps) {
  const [error, setError] = useState<string | null>(null);

  const fetchClientSecret = useCallback(async () => {
    try {
      // Refresh a stale/expired access token first - otherwise create-checkout
      // rejects the request with "Your session has expired".
      const { data: sessionData } = await supabase.auth.getSession();
      const expiresAt = sessionData.session?.expires_at ?? 0;
      if (sessionData.session && expiresAt * 1000 - Date.now() < 120_000) {
        await supabase.auth.refreshSession();
      }

      const { data, error: fnError } = await supabase.functions.invoke('create-checkout', {
        body: { module: moduleCode, quantity },
      });

      
      if (fnError) {
        console.error('Edge function error:', fnError);
        let message = 'Failed to start checkout. Please try again.';
        try {
          const ctx = (fnError as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const payload = await ctx.json();
            if (payload?.error) message = payload.error;
          }
        } catch {
          // keep default message
        }
        setError(message);
        throw new Error('Edge function error');
      }
      
      
      if (!data?.clientSecret) {
        console.error('No clientSecret in response:', data);
        setError(data?.error || 'Failed to create checkout session.');
        throw new Error('No client secret');
      }
      
      return data.clientSecret;
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Something went wrong. Please try again.');
      throw err;
    }
  }, [moduleCode, quantity]);

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-card rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative border border-border shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center text-muted-foreground"
        >
          <X className="w-4 h-4" />
        </button>
        
        {error ? (
          <div className="p-8 text-center">
            <p className="text-destructive mb-4">{error}</p>
            <button onClick={onClose} className="text-sm text-muted-foreground underline">Close</button>
          </div>
        ) : (
          <div className="p-1 min-h-[400px] flex flex-col">
            <EmbeddedCheckoutProvider stripe={getStripe()} options={{ fetchClientSecret }}>
              <EmbeddedCheckout className="flex-1" />
            </EmbeddedCheckoutProvider>
            <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground text-center px-4 py-3 border-t border-border/60 mt-1">
              <Heart className="w-3.5 h-3.5 text-accent shrink-0" aria-hidden="true" />
              5% of your payment is donated to The Ujamaa Children's Trust
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
